import { forwardRef, useImperativeHandle, useEffect, useState, ReactElement } from 'react';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { v4 as uuidv4 } from 'uuid';
import { UploadFile, DeleteFile, ACCEPTED_FILES, UPLOAD_TYPE } from '../storageAman';
import { IFile } from '../types/font.type';
import { randomString, getExtensionFromUrl, formatBytes } from '../utils';
import { UploadFileRef } from "./interfaces";

export interface IUploadFileProps {
  fontId: string;
  disable: boolean;
  files: IFile[];
}

const ProcessingUpload = ({ retryHandle, image, uploadFailedTitle }) => {
  return <div>
    {image && image.isErrorUpload &&
      <div style={{ cursor: 'pointer' }} onClick={retryHandle}>
        <span style={{ fontSize: 'small', color: '#dc3545' }}>{uploadFailedTitle}</span>
      </div>
    }
    {image.isProcessing &&
      <div style={{ padding: '5px 0px' }}>
        <ProgressBar animated now={image.progressing} label={`${image.progressing}%`} />
      </div>
    }
  </div>
};

const IconFileFont = ({ url }) => {
  const extension = getExtensionFromUrl(url);
  let icon: ReactElement<any, any>;
  switch (extension) {
    case ".woff2":
      icon = <i className="bi bi-file-earmark-font"></i>;
      break;
    case ".woff":
      icon = <i className="bi bi-filetype-woff"></i>;
      break;
    case ".ttf":
      icon = <i className="bi bi-filetype-ttf"></i>;
      break;
    case ".otf":
      icon = <i className="bi bi-filetype-otf"></i>;
      break;
    case ".eot":
      icon = <i className="bi bi-file-earmark-font"></i>;
      break;
    default:
      icon = <i className="bi bi-file-earmark-font"></i>;
  }
  return icon;
};

const UploadFileStorage = forwardRef<UploadFileRef, IUploadFileProps>(({ fontId, disable, files }, ref) => {
  const [dataFiles, setDataFiles] = useState<any[]>([]);
  const [urlDeletes, setUrlDeletes] = useState<string[]>([]);

  useImperativeHandle(ref, () => ({
    getFiles() {
      return dataFiles.map(item => {
        return {
          id: item.id || uuidv4(),
          fileName: item.fileName,
          fileSize: item.fileSize,
          fileUrl: item.id ? item.fileUrl : item.filePath,
          mediaLink: item.mediaLink,
          createdTime: item.id ? item.createdTime : new Date()
        };
      });
    },
    getUrlDeletes() {
      return urlDeletes;
    },
  }));

  useEffect(() => {
    setDataFiles(files);
  }, [files]);

  const onPressingHandle = (item: any, progressing: number) => {
    const foundIdx = dataFiles.findIndex(x => x.generateId === item.generateId);
    if (foundIdx >= 0) {
      dataFiles[foundIdx].isProcessing = true;
      dataFiles[foundIdx].progressing = progressing;
    }
    setDataFiles([...dataFiles]);
  };

  const onDownloadedHandle = (item: any, mediaLink: string, filePath: string) => {
    const foundIdx = dataFiles.findIndex(x => x.generateId === item.generateId);
    if (foundIdx >= 0) {
      dataFiles[foundIdx].isProcessing = false;
      dataFiles[foundIdx].isUploaded = true;
      dataFiles[foundIdx].mediaLink = mediaLink;
      dataFiles[foundIdx].filePath = filePath;
    }
    setDataFiles([...dataFiles]);
  };

  const onErrorUploadHandle = (item: any) => {
    const foundIdx = dataFiles.findIndex(x => x.generateId === item.generateId);
    if (foundIdx >= 0) {
      dataFiles[foundIdx].isProcessing = false;
      dataFiles[foundIdx].isUploaded = false;
      dataFiles[foundIdx].isErrorUpload = true;
    }
    setDataFiles([...dataFiles]);
  };

  const handleChangeFile = async (e: any) => {
    const files = Array.from(e.target.files)
      .map((item: any) => {
        const { name, size } = item;
        const currItem = item;
        currItem.generateId = `file_${randomString()}`;
        currItem.isProcessing = true;
        currItem.progressing = 0;
        currItem.fileName = name;
        currItem.fileSize = size;
        return currItem;
      });
    if (files.length === 0) {
      return;
    }
    files.forEach(file => {
      UploadFile(file, UPLOAD_TYPE.FONTS, null, (chunkIndex, totalChunks) => {
        const progress = Number(Number(chunkIndex * (100 / totalChunks)).toFixed(0));
        onPressingHandle(file, progress);
      }, (result) => {
        onDownloadedHandle(file, result.MediaLink, result.FilePath);
      }, () => {
        onErrorUploadHandle(file);
      });
    });
    dataFiles.push(...files);
    setDataFiles([...dataFiles]);
  }

  const retryFilesHandle = (item: any) => {
    const foundIdx = dataFiles.findIndex(x => x.generateId === item.generateId);
    if (foundIdx >= 0) {
      dataFiles[foundIdx].isProcessing = true;
      dataFiles[foundIdx].isUploaded = false;
      dataFiles[foundIdx].isErrorUpload = false;
      dataFiles[foundIdx].progressing = 0;
    }
    setDataFiles([...dataFiles]);
    UploadFile(item, UPLOAD_TYPE.FONTS, null, (chunkIndex, totalChunks) => {
      const progress = Number(Number(chunkIndex * (100 / totalChunks)).toFixed(0));
      onPressingHandle(item, progress);
    }, (result) => {
      onDownloadedHandle(item, result.MediaLink, result.FilePath);
    }, () => {
      onErrorUploadHandle(item);
    });
  };

  const removeFile = (item: any) => {
    if (item.id) {
      setUrlDeletes([...urlDeletes, item.fileUrl]);
    } else {
      DeleteFile(item.fileUrl, () => {}, () => {});
    }
    const foundIdx = dataFiles.findIndex(x => (x.id && x.id === item.id) || (x.generateId && x.generateId === item.generateId));
    if (foundIdx >= 0) {
      dataFiles.splice(foundIdx, 1);
    }
    setDataFiles([...dataFiles]);
  };

  const downloadFileAttach = (item: any) => {
    window.open(item.mediaLink, "_self");
  }

  return (
    <>
      <Form.Group as={Col} md="12" className="mb-3">
        <Form.Label>Files*</Form.Label>
        <Form.Control
          type="file"
          title=""
          multiple
          accept={ACCEPTED_FILES.FONTS}
          disabled={disable}
          onChange={handleChangeFile}
        />
      </Form.Group>
      <div className="class-item">
        {dataFiles.map((item, index) => (
          <div key={`key-file-${index}`} className="class-item-file">
            <span className="class-item-file-icon">
              {item.mediaLink &&
                <IconFileFont url={item.mediaLink} />
              }
            </span>
            <div className="class-item-file-name">
              {!item.isUploaded && !item.id &&
                <span>{item.fileName}</span>
              }
              {(item.isUploaded || item.id) &&
                <span className="class-item-file-text" onClick={() => downloadFileAttach(item)}>{item.fileName}</span>
              }
              {(item.isProcessing || item.isErrorUpload) &&
                <ProcessingUpload
                  image={item}
                  retryHandle={retryFilesHandle}
                  uploadFailedTitle={"Retry"}
                />
              }
              {(item.isUploaded || item.id) &&
                <span className="class-item-file-size">{formatBytes(item.fileSize)}</span>
              }
              <span className="class-item-file-icon-delete" onClick={() => removeFile(item)}>
                <i className="bi bi-x-circle-fill" style={{ color: '#dc3545' }}></i>
              </span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
},
);

export default UploadFileStorage;
